import { sharedErrorStringsEN } from 'shared/hooks/errorFormatters/sharedErrorStrings.en';

import { ClassifiedErrorType, classifyError } from './classifyError';

export interface ErrorDisplayInfo {
  type: ClassifiedErrorType;
  title: string;
  message: string;
  isRetryable: boolean;
  shouldLogout: boolean;
}

export interface ErrorDisplayStrings {
  defaultTitle: string;
  defaultMessage: string;
  errorCopy: Partial<
    Record<ClassifiedErrorType, { title: string; message: string }>
  >;
}

/**
 * English fallback used outside React (tests, imperative code). Components
 * should pass localized strings from useErrorDisplayStrings instead.
 */
export const defaultErrorDisplayStrings: ErrorDisplayStrings = {
  defaultTitle: sharedErrorStringsEN.title.default,
  defaultMessage: sharedErrorStringsEN.message.default,
  errorCopy: sharedErrorStringsEN.errorCopy,
};

/**
 * Converts any error into user-presentable display info, driven entirely
 * by classifyError. Always uses localized copy — never surfaces raw
 * backend messages to users.
 *
 * Auth errors intentionally fall back to default copy — they trigger the
 * logout flow and should never be presented as-is.
 */
export const getErrorDisplayInfo = (
  error: unknown,
  strings: ErrorDisplayStrings = defaultErrorDisplayStrings,
): ErrorDisplayInfo => {
  const classification = classifyError(error);
  const typeCopy = strings.errorCopy[classification.type];

  return {
    type: classification.type,
    title: typeCopy?.title ?? strings.defaultTitle,
    message: typeCopy?.message ?? strings.defaultMessage,
    isRetryable: classification.isRetryable,
    shouldLogout: classification.shouldLogout,
  };
};

/** Whether a retry action could plausibly succeed for this error */
export const shouldRetryError = (error: unknown): boolean =>
  classifyError(error).isRetryable;

/**
 * Whether this error indicates a session problem. Rarely needed — the
 * auth layer handles logout automatically for classified auth errors.
 */
export const shouldLogoutOnError = (error: unknown): boolean =>
  classifyError(error).shouldLogout;
